import React from 'react';
import { Property } from '../../../types';
import { 
    BedIcon, 
    BathIcon, 
    CarIcon, 
    RulerMeasureIcon, 
    ArrowsPointingOutIcon,
    ArrowsUpDownIcon,
    ListIcon, 
    InformationCircleIcon 
} from '../../Icons';
import { PropertyLocationMap } from '../../PropertyLocationMap';
import { PurchaseSimulator } from '../../PurchaseSimulator';
import { PropertyTagsList } from '../../broker/card/PropertyTagsList';

const formatArea = (val: string | number | null | undefined): string | null => {
    if (val === null || val === undefined) return null;
    const str = String(val).trim();
    if (!str || str === '0' || str === '0 m²') return null;
    return str.includes('m') ? str : `${str} m²`;
};

export const SharedPropertyInfo: React.FC<{ property: Property }> = ({ property }) => {
    const areaConstruida = formatArea(property.area_construida_original) || (property.area_construida ? `${property.area_construida} m²` : null);
    const areaPrivada = formatArea(property.area_privada);

    const hasElevator = (property.ascensor !== undefined && property.ascensor !== null && property.ascensor > 0) || 
        Boolean(property.features?.some(f => f.toLowerCase().includes('ascensor')));

    const elevatorVal = hasElevator 
        ? (property.ascensor && property.ascensor > 1 ? `${property.ascensor}` : 'Sí') 
        : 'No';

    const stats = [
        { label: 'Área Const.', val: areaConstruida || '--', icon: RulerMeasureIcon },
        { label: 'Área Priv.', val: areaPrivada || '--', icon: ArrowsPointingOutIcon },
        { label: 'Hab.', val: property.num_habitaciones, icon: BedIcon },
        { label: 'Baños', val: property.num_banos, icon: BathIcon },
        { label: 'Parq.', val: property.num_parqueaderos, icon: CarIcon },
        { label: 'Ascensor', val: elevatorVal, icon: ArrowsUpDownIcon, isHighlighted: hasElevator },
    ];

    return (
        <div className="space-y-12">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 py-8 border-y border-gray-100">
                {stats.map(s => (
                    <div key={s.label} className="text-center group p-2 rounded-xl transition-colors hover:bg-gray-50/80">
                        <s.icon className={`w-8 h-8 mx-auto mb-2 transition-transform group-hover:scale-110 ${s.isHighlighted ? 'text-emerald-600' : 'text-sky-600'}`} />
                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">{s.label}</p>
                        <p className={`text-lg font-black tracking-tighter ${s.isHighlighted ? 'text-emerald-700' : 'text-gray-800'}`}>
                            {s.val ?? '--'}
                        </p>
                    </div>
                ))}
            </div>

            {/* Tags del Inmueble */}
            {((property.tag && property.tag.length > 0) || (property.tags && property.tags.length > 0)) && (
                <div id={`shared-tags-container-${property.id || property.codigo}`} className="p-4 bg-gray-50/70 rounded-2xl border border-gray-100 flex items-center gap-3">
                    <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest shrink-0">Tags:</span>
                    <PropertyTagsList
                        tags={property.tag || property.tags}
                        maxVisible={10}
                        size="sm"
                        showIcon={true}
                        idPrefix={`shared-tags-${property.id || property.codigo}`}
                    />
                </div>
            )}

            {property.descripcion && (
                <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-sky-50 rounded-lg text-sky-700"><ListIcon className="w-5 h-5"/></div>
                        <h3 className="text-xl font-black uppercase tracking-tighter text-gray-800">Descripción</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed bg-gray-50/50 p-8 rounded-3xl border border-gray-100 whitespace-pre-wrap">
                        {property.descripcion}
                    </p>
                </section>
            )}

            {property.latitude && property.longitude && (
                <section className="space-y-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-sky-50 rounded-lg text-sky-700"><InformationCircleIcon className="w-5 h-5"/></div>
                        <h3 className="text-xl font-black uppercase tracking-tighter text-gray-800">Ubicación</h3>
                    </div>
                    <PropertyLocationMap 
                        latitude={property.latitude} 
                        longitude={property.longitude} 
                        propertyType={property.tipoinmueble} 
                        address={property.direccion}
                    />
                </section>
            )}

            <section className="pt-8">
                <PurchaseSimulator initialValue={property.precio_venta} />
            </section>
        </div>
    );
};